import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from './contexts/AuthContext.js';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider } from './contexts/AuthContext.js';

// Layouts
import MainLayout from './layouts/MainLayout';
import AuthLayout from './layouts/AuthLayout';

// Auth Pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';

// Dashboard Pages
import Dashboard from './pages/dashboard/Dashboard';
import Reports from './pages/reports/Reports';

// Patient Pages
import PatientList from './pages/patients/PatientList';
import PatientDetails from './pages/patients/PatientDetails';
import PatientForm from './pages/patients/PatientForm';

// Appointment Pages
import AppointmentView from './pages/appointments/AppointmentView';
import AppointmentDetails from './pages/appointments/AppointmentDetails';
import AppointmentForm from './pages/appointments/AppointmentForm';

// Medical Record Pages
import MedicalRecordList from './pages/medical-records/MedicalRecordList.js';
import MedicalRecordDetails from './pages/medical-records/MedicalRecordDetails.js';
import MedicalRecordForm from './pages/medical-records/MedicalRecordForm.js';

// Billing Pages
import BillingList from './pages/billing/BillingList.js';
import BillingDetails from './pages/billing/BillingDetails.js';
import BillingForm from './pages/billing/BillingForm.js';

// Inventory Pages
import InventoryList from './pages/inventory/InventoryList.js';
import InventoryDetails from './pages/inventory/InventoryDetails.js';
import InventoryForm from './pages/inventory/InventoryForm.js';

// Stocktake Pages
import StocktakeList from './pages/inventory/StocktakeList.js';
import StocktakeForm from './pages/inventory/StocktakeForm.js';
import StocktakeConduct from './pages/inventory/StocktakeConduct.js';
import StocktakeDetails from './pages/inventory/StocktakeDetails.js';

// Package Pages
import PackageManagement from './pages/PackageManagement.js';

// Admin Pages
import UserManagement from './pages/admin/UserManagement.js';
import HolidayManagement from './pages/admin/HolidayManagement.js';
import TaxConfiguration from './pages/admin/TaxConfiguration.js';
import AIConfiguration from './pages/admin/AIConfiguration.js';
import ClinicSettings from './pages/admin/ClinicSettings.js';

// Accounting Pages
import ChartOfAccounts from './pages/accounting/ChartOfAccounts.js';
import JournalEntries from './pages/accounting/JournalEntries.js';

// Debug Components
import AuthTest from './components/AuthTest.js';

// E-Invoice Components
import RecipientForm from './components/EInvoice/RecipientForm.js';
import SuccessPage from './components/EInvoice/SuccessPage.js';
import EInvoiceManagement from './components/EInvoice/EInvoiceManagement.js';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, currentUser } = useAuth();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (!isAuthenticated || !currentUser?.username) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <ToastContainer />
      <Router>
        <Routes>
          {/* Auth Routes */}
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<Login />} />
            {/* Register route removed - registration is now admin-only */}
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
          </Route>
          
          {/* Public E-Invoice Routes (no authentication required) */}
          <Route path="/einvoice/recipient/:qrToken" element={<RecipientForm />} />
          <Route path="/einvoice/success" element={<SuccessPage />} />
          
          {/* Protected Routes */}
          <Route element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }>
            {/* Dashboard */}
            <Route path="/" element={<Dashboard />} />
            <Route path="/reports" element={<Reports />} />
            
            {/* Patients */}
            <Route path="/patients" element={<PatientList />} />
            <Route path="/patients/new" element={<PatientForm />} />
            <Route path="/patients/:id" element={<PatientDetails />} />
            <Route path="/patients/:id/edit" element={<PatientForm />} />
            
            {/* Appointments */}
            <Route path="/appointments" element={<AppointmentView />} />
            <Route path="/appointments/new" element={<AppointmentForm />} />
            <Route path="/appointments/:id" element={<AppointmentDetails />} />
            <Route path="/appointments/:id/edit" element={<AppointmentForm />} />
            
            {/* Medical Records */}
            <Route path="/medical-records" element={<MedicalRecordList />} />
            <Route path="/medical-records/new" element={<MedicalRecordForm />} />
            <Route path="/medical-records/:id" element={<MedicalRecordDetails />} />
            <Route path="/medical-records/:id/edit" element={<MedicalRecordForm />} />
            
            {/* Billing */}
            <Route path="/billing" element={<BillingList />} />
            <Route path="/billing/new" element={<BillingForm />} />
            <Route path="/billing/:id" element={<BillingDetails />} />
            <Route path="/billing/:id/edit" element={<BillingForm />} />
            
            {/* E-Invoice Management */}
            <Route path="/einvoices" element={<EInvoiceManagement />} />
            
            {/* Inventory */}
            <Route path="/inventory" element={<InventoryList />} />
            <Route path="/inventory/new" element={<InventoryForm />} />
            <Route path="/inventory/:id" element={<InventoryDetails />} />
            <Route path="/inventory/:id/edit" element={<InventoryForm />} />
            
            {/* Stocktake */}
            <Route path="/inventory/stocktakes" element={<StocktakeList />} />
            <Route path="/inventory/stocktakes/new" element={<StocktakeForm />} />
            <Route path="/inventory/stocktakes/:id/conduct" element={<StocktakeConduct />} />
            <Route path="/inventory/stocktakes/:id" element={<StocktakeDetails />} />
            
            {/* Packages */}
            <Route path="/packages" element={<PackageManagement />} />
            
            {/* Admin Routes */}
            <Route path="/admin/users" element={<UserManagement />} />
            <Route path="/admin/holidays" element={<HolidayManagement />} />
            <Route path="/admin/tax-configuration" element={<TaxConfiguration />} />
            <Route path="/admin/ai-configuration" element={<AIConfiguration />} />
            <Route path="/admin/clinic-settings" element={<ClinicSettings />} />
            
            {/* Accounting Routes */}
            <Route path="/accounting/chart-of-accounts" element={<ChartOfAccounts />} />
            <Route path="/accounting/journal-entries" element={<JournalEntries />} />
            
            {/* Debug Routes */}
            <Route path="/auth-test" element={<AuthTest />} />
          </Route>
          
          {/* Fallback Route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
